import Axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import URLs from './configuration';
import { AddUser } from '../types';


Axios.defaults.timeout = 20000;
Axios.defaults.headers.common['Content-Type'] = 'application/json';

class Requests {
  private axios: AxiosInstance;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // private authAxios: any;
  private token: string | null;
  private expiresAt: string | null;
  // private preLoginToken: string | null;
  private authReady: boolean;
  private authReadyPromise: Promise<void>;
  private resolveAuthReady?: () => void;

  constructor() {
    console.log('Config.REACT_APP_API_BASE_URL', process.env.REACT_APP_API_BASE_URL);
    this.axios = Axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL,
      headers: {
        Origin: process.env.ALLOWED_ORIGIN,
      },
    });
    // this.authAxios = Axios.create({
    //   baseURL: process.env.REACT_APP_API_BASE_URL,
    //   headers: {
    //     Origin: process.env.ALLOWED_ORIGIN,
    //   },
    // });
    this.token = null;
    this.expiresAt = null;
    // this.preLoginToken = null;
    //add interceptor which decodes jwt token and check for expiry, if not expired, dont do anything,
    //if expired, then invoke api call to get fresh jwt token

    
    // Initialize auth readiness gate (blocked until explicitly marked ready)
    this.authReady = false;
    this.authReadyPromise = new Promise<void>((resolve) => {
      this.resolveAuthReady = resolve;
    });

    this.axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      // Block all outgoing requests until auth is marked ready
      if (!this.authReady) {
        await this.authReadyPromise;
      }
      // Attach Authorization header if we already have a token
      if (this.token) {
        (config.headers as Record<string, unknown>) = {
          ...(config.headers as Record<string, unknown>),
          Authorization: `Bearer ${this.token}`,
        };
      }
      return config;
    });
  }

  private base64UrlEncodeFromString = (input: string): string => {
    const bytes = new TextEncoder().encode(input);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private arrayBufferToBase64Url = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  getToken = () => {
    return this.token;
  }

  setToken = (jwtRes: { token: string; expiresAt?: string | null }) => {
    const { token, expiresAt } = jwtRes || {};
    this.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    this.token = token;
    this.expiresAt = expiresAt ?? null;
    try {
      window.dispatchEvent(new CustomEvent('backend-jwt-set'));
    } catch {
      // ignore
    }
  }

  clearToken = () => {
    delete this.axios.defaults.headers.common['Authorization'];
    this.token = null;
    this.expiresAt = null;
  }

  // Auth readiness controls
  markAuthReady = () => {
    if (!this.authReady) {
      this.authReady = true;
      this.resolveAuthReady?.();
    }
  }

  resetAuthReady = () => {
    this.authReady = false;
    this.authReadyPromise = new Promise<void>((resolve) => {
      this.resolveAuthReady = resolve;
    });
  }

  private decodeJwtExp = (token: string): number | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (typeof payload?.exp === 'number') return payload.exp; // seconds since epoch
      return null;
    } catch {
      return null;
    }
  }

  getUsers = (
    page: number,
    limit: number,
    options?: { searchString?: string; status?: string; role?: string }
  ) => {
    const params = new URLSearchParams({
      pageNumber: String(page),
      pageSize: String(limit),
    });

    if (options?.searchString) params.set('searchString', options.searchString);
    if (options?.status && options.status !== 'all') params.set('status', options.status);
    if (options?.role && options.role !== 'all') params.set('role', options.role);

    return this.axios.get(`${URLs.getUsers}?${params.toString()}`);
  };

  createUser = (data: AddUser) => {
    return this.axios.post(URLs.createUser, data);
  };

  updateUser = (userId: number, data: AddUser) => {
    return this.axios.post(`${URLs.updateUser}/${userId}`, data);
  };

  getUser = (userId: string) => {
    return this.axios.get(`${URLs.getUser}/${userId}`);
  };

  getUsersMetrics = () => {
    return this.axios.get(URLs.getUsersMetrics);
  };


  fetchLoggedInUser = () => {
    return this.axios.get(URLs.fetchLoggedInUser);
  };
}
export const API = new Requests();